import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import { useQuery } from 'react-query';

import Layout from 'containers/Layout';
import contentful from 'lib/contentful';
import { privatePage } from 'utils/auth';
import Account from 'components/layout/Account';
import { SiteSettingsData } from 'lib/contentful/types';
import sanfordApi from 'lib/sanford-api';
import { useState } from 'react';
import Order from 'components/orders/Order';
import NoOrder from 'components/orders/NoOrder';
import classnames from 'utils/classnames';
import { FillLinkButton } from 'components/Button';
import SpinnerIcon from 'components/icons/SpinnerIcon';
import { REVALIDATION_TIME } from '../../lib/constants';

const AccountOrdersPage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({ data }) => {
  const { siteSettings } = data;

  const ordersQuery = useQuery('ordersQuery', sanfordApi.getCustomerOrders);
  const orders = ordersQuery?.data?.data?.result?.orders;

  const [maxOrders, setMaxOrders] = useState(3);

  return (
    <Layout
      siteSettings={siteSettings}
      metaTags={[
        {
          name: 'robots',
          content: 'noindex',
        },
      ]}
      seo={{
        title: 'My Orders',
        description: 'All of your orders are listed here.',
        openGraphImageUrl: null,
      }}
    >
      <Account>
        <div className="flex flex-col md:h-full">
          {ordersQuery.isLoading && <SpinnerIcon className="m-auto animate-spin" />}
          {ordersQuery.isSuccess && (
            <>
              {orders.length === 0 ? (
                <p className="m-auto text-center"> {"You don't have any orders"}</p>
              ) : (
                <div className="flex flex-col gap-y-3 text-sf-blue-700">
                  <div>
                    <h1 className="text-base font-medium uppercase">Order history</h1>
                    <p className="text-sm">
                      Total orders <span className="font-medium">({orders.length})</span>
                    </p>
                  </div>
                  <div
                    className={classnames('', {
                      hidden: orders.length,
                    })}
                  >
                    <NoOrder />
                  </div>
                  <div className="mb-6 flex flex-col gap-y-6">
                    {orders.slice(0, maxOrders).map((order) => (
                      <Order key={order.id} order={order} />
                    ))}
                  </div>
                  <div
                    className={classnames('', {
                      hidden: maxOrders === orders.length,
                    })}
                  >
                    <div className="mb-3 flex items-center justify-center">
                      <FillLinkButton
                        buttonSize={'none'}
                        onClick={() => {
                          setMaxOrders(orders.length);
                        }}
                      >
                        <div className="w-full px-8 py-4 text-center uppercase">
                          Show all orders
                        </div>
                      </FillLinkButton>
                    </div>
                  </div>
                  <div>
                    <hr className="border-b border-sf-blue-700/5" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Account>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<{
  data: { siteSettings: SiteSettingsData };
}> = async () => {
  const siteSettings = await contentful.siteSettings.getSiteSettings();

  return {
    revalidate: REVALIDATION_TIME.SHORT,
    props: {
      data: { siteSettings },
    },
  };
};

export default privatePage(AccountOrdersPage);
